import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import { ListingWizard } from "#/components/sell/ListingWizard";
import { Button } from "#/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "#/components/ui/dialog";
import { useSignInModal } from "#/hooks/useSignInModal";
import { buildListingArgs } from "#/lib/listingFieldMap";
import {
	INITIAL_FORM_STATE,
	type ListingFormState,
	logisticsFromDefaults,
} from "#/lib/listingFormState";
import {
	isValidDiscIdentity,
	isValidFlightCondition,
} from "#/lib/listingFormValidation";
import { toast } from "#/lib/toast";

export const Route = createFileRoute("/sell")({ component: SellPage });

function SellPage() {
	const { t } = useTranslation("sell");
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open } = useSignInModal();
	const navigate = useNavigate();
	const createListing = useMutation(api.listings.createListing);
	const sellerDefaults = useQuery(api.users.getMySettings);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [publishedId, setPublishedId] = useState<string | null>(null);
	const [formKey, setFormKey] = useState(0);
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		if (!isDirty) return;
		const handler = (e: BeforeUnloadEvent) => {
			e.preventDefault();
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [isDirty]);

	useEffect(() => {
		if (
			!isLoading &&
			sellerDefaults !== undefined &&
			!sellerDefaults?.username
		) {
			navigate({ to: "/settings", search: { reason: "username-required" } });
		}
	}, [isLoading, sellerDefaults, navigate]);

	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);

	if (isLoading || sellerDefaults === undefined) return null;
	if (!isAuthenticated) return null;
	if (!sellerDefaults?.username) return null;

	const initialState: ListingFormState = {
		...INITIAL_FORM_STATE,
		logistics: logisticsFromDefaults(sellerDefaults),
	};

	async function handleSubmit(form: ListingFormState) {
		if (isSubmitting) return;
		if (!isValidDiscIdentity(form.discIdentity)) {
			toast.error(t("errors.discIdentityIncomplete"));
			return;
		}
		if (!isValidFlightCondition(form.flightCondition)) {
			toast.error(t("errors.flightConditionIncomplete"));
			return;
		}
		const discModelId = form.discIdentity.discModel?.id;
		setIsSubmitting(true);
		try {
			const listingId = await createListing(
				buildListingArgs(form, discModelId as Id<"discModels">),
			);
			setIsDirty(false);
			setPublishedId(listingId);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : t("errors.publishFailed"),
			);
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<>
			<Dialog
				open={publishedId !== null}
				onOpenChange={(open) => {
					if (!open) {
						setPublishedId(null);
						setFormKey((k) => k + 1);
						setIsDirty(false);
					}
				}}
			>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader className="items-center text-center gap-4 pt-2">
						<div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-3xl">
							🎉
						</div>
						<div className="flex flex-col gap-1">
							<DialogTitle
								className="text-2xl font-bold"
								style={{ fontFamily: "Fraunces, serif" }}
							>
								{t("published.title")}
							</DialogTitle>
							<DialogDescription className="text-sm">
								{t("published.description")}
							</DialogDescription>
						</div>
					</DialogHeader>
					<DialogFooter className="flex-col sm:flex-col gap-2 pt-2">
						<Button
							className="w-full"
							onClick={() =>
								navigate({
									to: "/listings/$id",
									params: { id: publishedId ?? "" },
								})
							}
						>
							{t("published.viewListing")}
						</Button>
						<Button
							variant="outline"
							className="w-full"
							onClick={() => {
								setPublishedId(null);
								setFormKey((k) => k + 1);
								setIsDirty(false);
							}}
						>
							{t("published.addAnother")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<ListingWizard
				key={formKey}
				initialState={initialState}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				onDirty={() => setIsDirty(true)}
				onClose={() => navigate({ to: "/" })}
			/>
		</>
	);
}
